import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react'

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string
  /** Show a reveal toggle. Only meaningful for type="password". */
  revealable?: boolean
}

/**
 * DESIGN.md text-input: canvas ground, 12px radius, 48px tall, 1px hairline
 * border thickening to a 2px primary border on focus.
 */
export function TextField({
  label,
  hint,
  error,
  revealable = false,
  type = 'text',
  className = '',
  ...rest
}: TextFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const [revealed, setRevealed] = useState(false)

  const effectiveType = revealable && revealed ? 'text' : type
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className}>
      <label htmlFor={id} className="t-title-sm block text-ink mb-2">
        {label}
      </label>

      <div className="relative">
        <input
          {...rest}
          id={id}
          type={effectiveType}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={[
            't-body-md w-full h-12 rounded-[12px] bg-canvas text-ink',
            'px-4 outline-none transition-[border-color,box-shadow] duration-150',
            'placeholder:text-muted-soft',
            revealable ? 'pr-24' : '',
            error
              ? 'border-2 border-semantic-down'
              : 'border border-hairline focus:border-2 focus:border-primary',
            // Offset 1px of padding when the border thickens so text does not jump
            error ? 'px-[15px]' : 'focus:px-[15px]',
            'disabled:bg-surface-soft disabled:text-muted disabled:cursor-not-allowed',
          ].join(' ')}
        />

        {revealable && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="t-caption-strong absolute right-3 top-1/2 -translate-y-1/2
                       rounded-[100px] px-3 py-1.5 text-body
                       hover:bg-surface-strong active:bg-hairline transition-colors"
            // Helper control — keep it out of the sign-in form's tab order.
            tabIndex={-1}
          >
            {revealed ? 'Hide' : 'Show'}
          </button>
        )}
      </div>

      {error && (
        <p id={errorId} className="t-caption mt-2 text-semantic-down">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="t-caption mt-2 text-muted">
          {hint}
        </p>
      )}
    </div>
  )
}
